/**
 * Every course on the platform, and who is allowed to see it.
 *
 * PLATFORM-WIDE, which is what makes this different from the tenant admin's course list:
 * that one is scoped to one academy's orgs, this one is scoped to nothing. The reason it
 * can be is measured rather than assumed — Studio's course listing filters by what the
 * REQUESTING USER may read, not by the request's Site. On the console hostname (which
 * resolves to no tenant at all) a superuser gets `count: 3` covering every org on the
 * install, while the LMS catalogue endpoint on the same hostname returns `count: 0`
 * because `get_visible_courses()` finds no `course_org_filter` to work with. So the CMS
 * endpoint is the only built-in one that answers this page's question.
 *
 * The endpoint is the platform's own — `GET /api/contentstore/v2/home/courses`, the same
 * call `frontend-app-authoring`'s studio-home makes, already wrapped by
 * `shared/lib/studio.listCourses`. Nothing here is a custom listing API, and the only
 * endpoint of ours involved is the licence WRITE.
 *
 * ACTIVE COURSES ONLY, matching Studio's default tab. An archived course is one whose run
 * has ended; licensing one would put a closed course in a catalogue, so it is not offered
 * until somebody asks for it.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Dialog,
  Icon,
  SearchField,
  Spinner,
  Stat,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { PageHeader } from '../../shared/layout/WorkspaceShell';
import { ApiError } from '../../shared/lib/openedx';
import { listCourses, type StudioCourse } from '../../shared/lib/studio';
import { setCourseLicences, tenantsApi, type Tenant } from '../api/tenants';

/** Matches the tenant admin's listing, so the two pages page at the same rhythm. */
const PAGE_SIZE = 24;

export default function CourseList() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  /** The course whose licences are being edited, or null. */
  const [licensing, setLicensing] = useState<StudioCourse | null>(null);

  const courses = useQuery({
    queryKey: ['platform-courses', search, page],
    queryFn: () => listCourses({ search: search || undefined, page, pageSize: PAGE_SIZE }),
  });

  // The tenant list is what turns a course key into "who can see this". Kept as its own
  // query rather than folded into the course one: it is small, it changes rarely, and the
  // licence dialog re-reads it after every write.
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: tenantsApi.list });

  const rows = courses.data?.courses ?? [];
  const pages = courses.data?.num_pages ?? 1;
  const all: Tenant[] = tenants.data ?? [];

  const columns: Column<StudioCourse>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Course',
        render: (course) => (
          <>
            <strong>{course.display_name}</strong>
            {/* The key, always visible: it is what an operator will paste into a support
                ticket, and the thing a licence is actually stored against. */}
            <div className="admin__sub">{course.course_key}</div>
          </>
        ),
      },
      { key: 'org', header: 'Organisation', render: (course) => course.org, mono: true },
      { key: 'run', header: 'Run', render: (course) => course.run, mono: true },
      {
        key: 'visible',
        header: 'Visible to',
        render: (course) => <Audience course={course} tenants={all} />,
      },
      {
        key: 'actions',
        header: 'Licensing',
        render: (course) => (
          <Button variant="ghost" onClick={() => setLicensing(course)}>
            <Icon name="key" />
            Licence
          </Button>
        ),
      },
    ],
    [all],
  );

  if (courses.isLoading) return <Spinner label="Loading courses…" />;

  if (courses.isError) {
    return (
      <Banner tone="danger">
        <strong>Could not load courses.</strong> Studio did not answer, so this page cannot
        say what exists. Nothing has changed — retry, and check that the CMS is running.
      </Banner>
    );
  }

  const licensedTotal = all.reduce((total, tenant) => total + tenant.licensed_courses.length, 0);

  return (
    <>
      <PageHeader
        title="Courses"
        description="Every course on the platform, whichever academy owns it. Licence one to an academy that does not own its organisation and it appears in that academy's catalogue."
      />

      <div className="console__stats">
        <Stat label="Courses" value={courses.data?.count ?? 0} />
        <Stat label="Organisations" value={new Set(rows.map((c) => c.org)).size} />
        <Stat label="Academies" value={all.length} />
        <Stat label="Licences granted" value={licensedTotal} />
      </div>

      <Toolbar>
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search courses…"
        />
      </Toolbar>

      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(course) => course.course_key}
          empty={
            search
              ? 'No course matches that search.'
              : 'No courses exist yet. Courses are created inside an academy, not here.'
          }
        />
        {pages > 1 && (
          <div className="admin__pager">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <Icon name="chevron_left" />
              Previous
            </Button>
            <span>
              Page {page} of {pages} · {courses.data?.count ?? 0} courses
            </span>
            <Button variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Next
              <Icon name="chevron_right" />
            </Button>
          </div>
        )}
      </Card>

      {licensing && (
        <LicenceDialog
          course={licensing}
          tenants={all}
          onClose={() => setLicensing(null)}
        />
      )}
    </>
  );
}

/** Tenants that own the course's org. They need no licence and cannot be given one. */
function ownersOf(course: StudioCourse, tenants: Tenant[]): Tenant[] {
  const org = course.org.toUpperCase();
  return tenants.filter((tenant) => tenant.orgs.some((o) => o.toUpperCase() === org));
}

function licenseesOf(course: StudioCourse, tenants: Tenant[]): Tenant[] {
  return tenants.filter((tenant) => tenant.licensed_courses.includes(course.course_key));
}

/**
 * Who can currently see this course, and why.
 *
 * The distinction is the point: an owner sees it because the academy holds the whole
 * organisation, a licensee sees exactly this course. Collapsing the two into one list of
 * names would leave an operator unable to tell what revoking a licence would do.
 */
function Audience({ course, tenants }: { course: StudioCourse; tenants: Tenant[] }) {
  const owners = ownersOf(course, tenants);
  const licensees = licenseesOf(course, tenants);

  if (owners.length === 0 && licensees.length === 0) {
    // Reachable and worth saying out loud: a course whose org belongs to no academy is
    // invisible everywhere, which looks like a broken course rather than a missing
    // `course_org_filter` entry.
    return <Badge tone="warning">No academy</Badge>;
  }

  return (
    <div className="admin__badges">
      {owners.map((tenant) => (
        <Badge key={tenant.slug} tone="info">
          {tenant.name} · owns org
        </Badge>
      ))}
      {licensees.map((tenant) => (
        <Badge key={tenant.slug} tone="success">
          {tenant.name} · licensed
        </Badge>
      ))}
    </div>
  );
}

/**
 * Grant and revoke in one dialog.
 *
 * The saved value is the whole set of ticked academies, which is what the endpoint takes —
 * so unticking is a revocation and there is no separate remove action to forget. Owners are
 * listed as fixed rows rather than hidden: an operator looking for "why can they see it"
 * should find the answer in the same place as the switches.
 */
function LicenceDialog({
  course,
  tenants,
  onClose,
}: {
  course: StudioCourse;
  tenants: Tenant[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const owners = ownersOf(course, tenants);
  const ownerSlugs = new Set(owners.map((tenant) => tenant.slug));
  const licensable = tenants.filter((tenant) => !ownerSlugs.has(tenant.slug));

  const [selected, setSelected] = useState<string[]>(() =>
    licenseesOf(course, tenants).map((tenant) => tenant.slug),
  );

  const save = useMutation({
    mutationFn: () => setCourseLicences(course.course_key, selected),
    onSuccess: () => {
      // The tenant list is the source of the "Visible to" column, so it has to be re-read
      // before the dialog closes or the table would still show the previous audience.
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onClose();
    },
  });

  const toggle = (slug: string) =>
    setSelected((current) =>
      current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug],
    );

  return (
    <Dialog
      title="Licence this course"
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save licences
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <dl className="console__facts">
        <dt>Course</dt>
        <dd>{course.display_name}</dd>
        <dt>Course ID</dt>
        <dd className="mono">{course.course_key}</dd>
        <dt>Organisation</dt>
        <dd className="mono">{course.org}</dd>
      </dl>

      {save.isError && <LicenceError error={save.error} />}

      <ul className="console__flags">
        {owners.map((tenant) => (
          <li key={tenant.slug}>
            <label>
              <input type="checkbox" checked disabled />
              {tenant.name}
            </label>
            <span className="console__inherited">owns {course.org}</span>
            <p>
              Sees this course through its organisation, so it needs no licence and cannot
              be given one. Remove the organisation from the academy to change that.
            </p>
          </li>
        ))}

        {licensable.map((tenant) => (
          <li key={tenant.slug}>
            <label>
              <input
                type="checkbox"
                checked={selected.includes(tenant.slug)}
                onChange={() => toggle(tenant.slug)}
              />
              {tenant.name}
            </label>
            <span className="console__inherited">{tenant.orgs.join(', ')}</span>
            <p>
              {selected.includes(tenant.slug)
                ? 'Appears in this academy’s catalogue and on its course page.'
                : 'Not licensed. Learners here cannot find or open this course.'}
            </p>
          </li>
        ))}
      </ul>

      {licensable.length === 0 && (
        <p className="console__hint">
          Every academy already owns this course’s organisation, so there is nothing to
          licence.
        </p>
      )}
    </Dialog>
  );
}

/** Say what the server refused and what state that leaves behind. */
function LicenceError({ error }: { error: unknown }) {
  const detail =
    error instanceof ApiError && typeof (error.body as { detail?: string })?.detail === 'string'
      ? (error.body as { detail: string }).detail
      : null;

  return (
    <Banner tone="danger">
      <strong>The licences were not saved.</strong> {detail ?? 'The tenancy service refused the change.'}{' '}
      Nothing changed, so the academies below still see what they saw before.
    </Banner>
  );
}
